import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCheck } from "lucide-react";

export function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const mutingRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      if (Date.now() < mutingRef.current) return; // don't clobber a just-made local change
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = (n) => {
    setOpen(false);
    if (!n.read) {
      mutingRef.current = Date.now() + 2500;
      setUnread((u) => Math.max(0, u - 1));
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const markAll = () => {
    mutingRef.current = Date.now() + 2500;
    setUnread(0);
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
    api.post("/notifications/read-all").catch(() => {});
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" aria-label="Notifications" className="relative rounded-lg p-2 text-[#5B21B6] transition-colors hover:bg-[#F5F3FF]">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notif-count" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-[#E11D48] px-1 text-[10px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-[#EEEAF8] px-4 py-3">
          <span className="font-heading font-bold text-[#1E1B2E]">Notifications</span>
          {items.some((x) => !x.read) && (
            <button onClick={markAll} data-testid="notif-mark-all" className="inline-flex items-center gap-1 text-xs font-semibold text-[#7C3AED] hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[#948EAB]">No notifications yet.</div>
          ) : (
            items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`}
                className={`flex w-full flex-col items-start gap-0.5 border-b border-[#F5F3FF] px-4 py-3 text-left transition-colors hover:bg-[#FAF9FE] ${n.read ? "" : "bg-[#F5F3FF]"}`}>
                <div className="flex w-full items-center gap-2">
                  {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-[#7C3AED]" />}
                  <span className="text-sm font-bold text-[#1E1B2E]">{n.title}</span>
                </div>
                <span className="text-xs leading-relaxed text-[#524B6B]">{n.body}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
